import { text_colorTextPrimary } from 'ext-ui/style/colors'
import IntegrationsCodeBlockIcon from 'ext-ui/svg/icon/IntegrationsCodeBlockIcon'
import ManageSubscriptionPromoSvg from 'ext-ui/svg/illustration/ManageSubscriptionPromoSvg'
import React from 'react'
import ManageSubscriptionPromoCard from 'src/app/ManageSubscription/components/ManageSubscriptionPromo/ManageSubscriptionPromoCard/ManageSubscriptionPromoCard'
import QuickBooks from 'src/assets/images/QuickBooks.svg'
import NetSuite from 'src/assets/svgs/NetSuiteIcon.svg'
import { useSaasUpgradeAction } from 'src/hooks/useSaasUpgradeAction'
import { useTranslate } from 'src/localization/Trns'
import { getSaasTierLabel, SaasTierType } from 'src/models/organizations.models'
import Lnk from 'src/navigation/Lnk'
import { PRICING_ROUTE } from 'src/navigation/urls'

import styles from './ManageSubscriptionPromo.style'

interface ManageSubscriptionPromoProps {
  organizationId: string
  currentTier: SaasTierType
}
const ManageSubscriptionPromo: React.FC<ManageSubscriptionPromoProps> = (
  props,
) => {
  const translate = useTranslate()

  return (
    <div css={styles} className={'ManageSubscriptionPromo'}>
      <div className={'flex-column gap-3'}>
        {/*TODO: best way to make these dynamic?*/}
        <span className={'title'}>
          {translate('Get More out of your Card')}
        </span>
        <span className={'description'}>
          {props.currentTier === 'STARTER' &&
            translate(
              'Upgrade to Pro to access advanced features like 500 virtual cards* per month, custom approval workflows, and enhanced integrations. Give your team the tools to scale efficiently and securely.',
            )}
          {props.currentTier === 'PRO' &&
            translate(
              'Enterprise gives you the ultimate flexibility and control, with unlimited virtual cards* & expense categories, premium integrations, and priority support. Perfect for organizations managing complex workflows and higher transaction volumes.',
            )}
          {props.currentTier === 'ENTERPRISE' &&
            translate(
              'Extend your Enterprise plan with powerful add-ons like advanced APIs, custom reporting, and a designated account manager. Only pay for what your business needs to maximize value.',
            )}
        </span>
        {(props.currentTier === 'STARTER' || props.currentTier === 'PRO') && (
          <span className={'disclaimer'}>
            {translate('*Virtual cards available for ${selectIssuersLink}', {
              selectIssuersLink: (
                <Lnk
                  className="underline-link"
                  href="https://support.paywithextend.com/hc/en-us/sections/35632429037079-Registering-a-Card-Account"
                  ariaLabel={translate('select issuers')}
                >
                  {translate('select issuers')}
                </Lnk>
              ),
            })}
          </span>
        )}
      </div>
      <div className={'promo-cards'}>
        {props.currentTier === 'STARTER' && (
          <StarterPromoCards orgId={props.organizationId} />
        )}
        {props.currentTier === 'PRO' && (
          <ProPromoCards orgId={props.organizationId} />
        )}
        {props.currentTier === 'ENTERPRISE' && (
          <EnterprisePromoCards orgId={props.organizationId} />
        )}
      </div>
    </div>
  )
}

export default ManageSubscriptionPromo

/**
 * TODO: we need to use a mapping of UserFeature to strings, so that we can leverage
 * the Features endpoint and userFeatureRequiredTier temp const to figure out what
 * promo cards to show per tier
 */

const StarterPromoCards: React.FC<{ orgId: string }> = ({ orgId }) => {
  const translate = useTranslate()
  const nextTier: SaasTierType = 'PRO'
  const nextTierLabel = translate(getSaasTierLabel(nextTier))
  const upgradeAction = useSaasUpgradeAction(orgId)
  return (
    <>
      <ManageSubscriptionPromoCard
        className={'large-card'}
        hero={
          <div className={'flex-row justify-center align-center w-full'}>
            <ManageSubscriptionPromoSvg height={120} className={'hero-image'} />
          </div>
        }
        title={translate('Expense Management with ${tier}', {
          tier: nextTierLabel,
        })}
        description={translate(
          'Pro gives you the insights and tools to manage company spend with confidence. Get detailed visibility into transactions, streamline expense workflows, and unlock automation that keeps your team on track without extra effort.',
        )}
        learnMoreLink={{
          href: 'https://support.paywithextend.com/hc/en-us/categories/28575242187927',
        }}
        buttonCta={{
          label: translate('Get ${tier}', { tier: nextTierLabel }),
          'data-testid': `upgrade-to-${nextTierLabel}-button`.toLowerCase(),
          onClick: () => {
            upgradeAction(
              nextTier,
              'Manage Subscription - Generic Upgrade to Pro',
            )
          },
        }}
      />
      <ManageSubscriptionPromoCard
        className={'small-card'}
        hero={<img className="integration-logo" src={QuickBooks} alt={''} />}
        title={translate('Direct Sync to QuickBooks')}
        pill={nextTierLabel}
        description={translate(
          'Push cleared transactions into QuickBooks Online for fast, error-free reconciliation. No more manual uploads - ${tier} makes sure your books are always accurate and up to date.',
          {
            tier: nextTierLabel,
          },
        )}
        learnMoreLink={{
          href: 'https://support.paywithextend.com/hc/en-us/sections/28621862973463-Quickbooks',
        }}
        largeCta={{
          title: translate('Upgrade to Extend ${tier}', {
            tier: nextTierLabel,
          }),
          onClick: () => {
            upgradeAction(
              nextTier,
              'Manage Subscription - Direct Sync to QuickBooks',
            )
          },
        }}
      />
      <ManageSubscriptionPromoCard
        headerBorder
        className={'medium-card'}
        title={translate('Never Chase Receipts Again')}
        pill={nextTierLabel}
        description={translate(
          '${tier} automatically matches receipts to transactions, saving your team hours of manual work. Upload once and let Extend handle the rest - your expense reports stay complete and audit-ready.',
          {
            tier: nextTierLabel,
          },
        )}
        learnMoreLink={{
          href: 'https://support.paywithextend.com/hc/en-us/sections/28621388103063-Receipt-Rules',
        }}
        buttonCta={{
          label: translate('Get ${tier}', { tier: nextTierLabel }),
          'data-testid': `upgrade-to-${nextTierLabel}-button-2`.toLowerCase(),
          variant: 'toolbar',
          onClick: () => {
            upgradeAction(
              nextTier,
              'Manage Subscription - Receipt Rules and Matching',
            )
          },
        }}
      />
      <ManageSubscriptionPromoCard
        headerBorder
        className={'medium-card'}
        title={translate('Smarter Expense Coding')}
        pill={nextTierLabel}
        description={translate(
          'Set up rules that auto-categorize expenses by department, project, budget, or merchant. With ${tier}, transactions code themselves - so your finance team can focus on strategy, not spreadsheets.',
          {
            tier: nextTierLabel,
          },
        )}
        learnMoreLink={{
          href: 'https://support.paywithextend.com/hc/en-us/articles/28685034208919-Creating-expense-category-rules',
        }}
        buttonCta={{
          label: translate('Get ${tier}', { tier: nextTierLabel }),
          'data-testid': `upgrade-to-${nextTierLabel}-button-3`.toLowerCase(),
          variant: 'toolbar',
          onClick: () => {
            upgradeAction(
              nextTier,
              'Manage Subscription - Expense Category Rules',
            )
          },
        }}
      />
    </>
  )
}

const ProPromoCards: React.FC<{ orgId: string }> = ({ orgId }) => {
  const translate = useTranslate()
  const nextTier: SaasTierType = 'ENTERPRISE'
  const nextTierLabel = translate(getSaasTierLabel(nextTier))
  const upgradeAction = useSaasUpgradeAction(orgId)
  return (
    <>
      <ManageSubscriptionPromoCard
        className={'large-card'}
        hero={
          <div className={'flex-row justify-center align-center w-full'}>
            <ManageSubscriptionPromoSvg height={120} className={'hero-image'} />
          </div>
        }
        title={translate('Upgrade to ${tier} for Maximum Control', {
          tier: nextTierLabel,
        })}
        description={translate(
          'When Pro isn’t enough, Enterprise has your back. Unlock advanced integrations, thousands of virtual cards* per month, tailored workflows, and powerful reporting - giving finance teams the scale and flexibility to stay ahead.',
        )}
        disclaimer={translate(
          '*Virtual cards available for ${selectIssuersLink}',
          {
            selectIssuersLink: (
              <Lnk
                className="underline-link"
                href="https://support.paywithextend.com/hc/en-us/sections/35632429037079-Registering-a-Card-Account"
                ariaLabel={translate('select issuers')}
              >
                {translate('select issuers')}
              </Lnk>
            ),
          },
        )}
        learnMoreLink={{
          to: PRICING_ROUTE(),
        }}
        buttonCta={{
          label: translate('Contact Us'),
          onClick: () => {
            upgradeAction(
              nextTier,
              'Manage Subscription - Generic Pro to Enterprise Upgrade',
            )
          },
        }}
      />
      <ManageSubscriptionPromoCard
        className={'small-card'}
        hero={<img className="integration-logo" src={NetSuite} alt={''} />}
        title={translate('Seamless NetSuite Sync')}
        pill={nextTierLabel}
        description={translate(
          'Push cleared transactions directly into NetSuite for fast, accurate reconciliation. Eliminate manual entry and ensure your books stay clean and audit-ready with every sync.',
        )}
        learnMoreLink={{
          href: 'https://support.paywithextend.com/hc/en-us/sections/28582056451735-NetSuite',
        }}
        largeCta={{
          title: translate('Upgrade to Extend ${tier}', {
            tier: nextTierLabel,
          }),
          onClick: () => {
            upgradeAction(
              nextTier,
              'Manage Subscription - Pro to Enterprise Upgrade for NetSuite Integration',
            )
          },
        }}
      />
      <ManageSubscriptionPromoCard
        headerBorder
        className={'medium-card'}
        title={translate('Build Custom Workflows')}
        pill={nextTierLabel}
        description={translate(
          'Generate your API key to embed Extend into your systems. Automate card creation, approvals, monitoring, and expense processing - so you can scale without adding manual overhead',
        )}
        learnMoreLink={{
          href: 'https://www.paywithextend.com/solutions/integrated-payments',
        }}
        buttonCta={{
          label: translate('Contact Us'),
          variant: 'toolbar',
          onClick: () => {
            upgradeAction(
              nextTier,
              'Manage Subscription - Pro to Enterprise Upgrade for API Keys, Custom Workflows',
            )
          },
        }}
      />
      <ManageSubscriptionPromoCard
        headerBorder
        className={'medium-card'}
        title={translate('Built for Complex Organizations')}
        pill={nextTierLabel}
        description={translate(
          'Enterprise is made for teams that need more: custom controls, deeper visibility, and the flexibility to shape Extend around your unique workflows. Scale with confidence, no matter how complex things get.',
        )}
        buttonCta={{
          label: translate('Contact Us'),
          variant: 'toolbar',
          onClick: () => {
            upgradeAction(
              nextTier,
              'Manage Subscription - Pro to Enterprise Upgrade for Custom Controls and Flexibility',
            )
          },
        }}
      />
    </>
  )
}

// TODO: add the ability to show 'Add-On' cards for Enterprise Tier, and show whether or not they have the add-on already, once supported by backend
const EnterprisePromoCards: React.FC<{ orgId: string }> = ({ orgId }) => {
  const translate = useTranslate()
  const tier: SaasTierType = 'ENTERPRISE'
  const upgradeAction = useSaasUpgradeAction(orgId)
  return (
    <>
      <ManageSubscriptionPromoCard
        className={'large-card'}
        hero={
          <div className={'flex-row justify-center align-center w-full'}>
            <ManageSubscriptionPromoSvg height={120} className={'hero-image'} />
          </div>
        }
        title={translate('Complete Expense Control Suite')}
        pill={translate('Add On')}
        description={translate(
          'The complete expense management solution for enterprises ready to scale. This comprehensive bundle eliminates the complexity of managing multiple vendors and integrations by delivering everything your finance team needs in one powerful package. Get unlimited users, up to 2,000 virtual cards* monthly, custom expense categories, and seamless NetSuite integration—all in one cost-effective solution.',
        )}
        disclaimer={translate(
          '*Virtual cards available for ${selectIssuersLink}',
          {
            selectIssuersLink: (
              <Lnk
                className="underline-link"
                href="https://support.paywithextend.com/hc/en-us/sections/35632429037079-Registering-a-Card-Account"
                ariaLabel={translate('select issuers')}
              >
                {translate('select issuers')}
              </Lnk>
            ),
          },
        )}
        buttonCta={{
          label: translate('Contact Us'),
          onClick: () => {
            upgradeAction(
              tier,
              'Manage Subscription - Generic Add On Inquiry for Enterprise',
            )
          },
        }}
      />
      <ManageSubscriptionPromoCard
        className={'small-card'}
        hero={<IntegrationsCodeBlockIcon color={text_colorTextPrimary} />}
        title={translate('Unlock Powerful Integrations')}
        pill={translate('Add On')}
        description={translate(
          'Open up Extend’s full potential with a secure API key. Automate card creation, streamline approvals, monitor transactions in real time, and embed expense management directly into your own systems.',
        )}
        largeCta={{
          title: translate('Contact Us'),
          onClick: () => {
            upgradeAction(
              tier,
              'Manage Subscription - Enterprise Add On for API Keys',
            )
          },
        }}
        learnMoreLink={{
          href: 'https://www.paywithextend.com/solutions/integrated-payments',
        }}
      />
      <ManageSubscriptionPromoCard
        headerBorder
        className={'medium-card'}
        title={translate('Scale Spend Without Limits')}
        pill={translate('Add On')}
        description={translate(
          'Go beyond standard issuance with the ability to create thousands of virtual cards* for employees, vendors, or projects. Manage spend at scale while keeping tight controls on limits, categories, and approvals—perfect for high-volume organizations.',
        )}
        disclaimer={translate(
          '*Virtual cards available for ${selectIssuersLink}',
          {
            selectIssuersLink: (
              <Lnk
                className="underline-link"
                href="https://support.paywithextend.com/hc/en-us/sections/35632429037079-Registering-a-Card-Account"
                ariaLabel={translate('select issuers')}
              >
                {translate('select issuers')}
              </Lnk>
            ),
          },
        )}
        buttonCta={{
          label: translate('Contact Us'),
          variant: 'toolbar',
          onClick: () => {
            upgradeAction(
              tier,
              'Manage Subscription - Enterprise Add On for Virtual Card Limit Increase',
            )
          },
        }}
      />
      <ManageSubscriptionPromoCard
        headerBorder
        className={'medium-card'}
        title={translate('Insights That Fit Your Business')}
        pill={translate('Add On')}
        description={translate(
          'Reports that work the way you do. Break down spend across teams, vendors, or categories and surface insights tailored to your finance workflows—so you can spot trends, tighten budgets, and stay audit-ready.',
        )}
        buttonCta={{
          label: translate('Contact Us'),
          variant: 'toolbar',
          onClick: () => {
            upgradeAction(
              tier,
              'Manage Subscription - Enterprise Add On for Insights',
            )
          },
        }}
      />
    </>
  )
}
